// Los textos que el comerciante sobreescribe, en un módulo aparte del
// diccionario.
//
// Por qué está separado de i18n.ts: `personalizer-settings.ts` sólo
// necesita `setTextOverrides`, pero es de los pocos módulos que SÍ
// viajan en el entry del storefront (aplica el color de marca y la
// etiqueta del botón antes del primer clic). Importarlo desde
// `../i18n.js` arrastraba los dos diccionarios completos —ES + EN, ~21
// KB minificados— a `embed.js`, que se descarga en cada page-view de
// ficha de producto. Rollup no puede tirarlos: son objetos literales de
// nivel superior con efectos observables para él.
//
// Aquí no hay más que un Record y su saneador, así que el entry paga
// unos cientos de bytes en vez de 21 KB. `i18n.ts` reexporta
// `setTextOverrides` para que los call sites viejos sigan compilando.
// Ver bundle-budget.test.ts.

// Capa de máxima prioridad de la cadena de copy:
//   settings.texts[key]  →  dict[locale][key]  →  ES[key]  →  key
// La rellena applyPersonalizerSettings desde el blob
// Shop.personalizerSettings, así que cada string que pasa por t() es
// editable por el comerciante sin redeploy. Las claves son las del
// Dict verbatim — la sección «Textos» del admin las escribe 1:1.
let textOverrides: Record<string, string> = {};

/** Replace the merchant-override table. Non-string / empty values are
 *  dropped (empty = "use the built-in default", not "blank it out"). */
export function setTextOverrides(raw: unknown): void {
  const out: Record<string, string> = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim() !== '') out[k] = v;
    }
  }
  textOverrides = out;
}

/** El override del comerciante para `key`, o undefined si no hay. */
export function getTextOverride(key: string): string | undefined {
  return textOverrides[key];
}

// ── El idioma activo ─────────────────────────────────────────────────
//
// Vive AQUÍ y no en `i18n.ts` por lo mismo que la tabla de arriba: lo
// necesita `personalizer-settings.ts`, que viaja en el entry, para saber
// si el copy que el comerciante escribió aplica en el idioma que el
// cliente está viendo. Importarlo de `../i18n.js` arrastraría los tres
// diccionarios a `embed.js`.
//
// Es UN solo estado: `resolveLocale` lo escribe y `t()` lo lee de aquí,
// así que no puede haber dos ideas del idioma activo — que es justo el
// fallo que este fichero vino a evitar con los textos.
let localeActivo = 'es';
let etiquetaActiva = 'es';

/** Lo llama `detectarYFijarLocale` (aquí abajo) y nadie más.
 *
 *  Dos valores porque son DOS COSAS: el `code` de dos letras es el
 *  diccionario que se va a usar —tiene que existir— y la `etiqueta` es
 *  lo que la página dice que es («pt-PT»), que además lleva el PAÍS.
 *  Esa región la necesita el geocodificador para desempatar entre dos
 *  sitios que se llaman igual, y no se puede deducir del idioma: pt-PT
 *  y pt-BR son la misma lengua en dos hemisferios. */
export function fijarLocaleActivo(code: string, etiqueta?: string): void {
  localeActivo = code;
  etiquetaActiva = etiqueta || code;
}

/** Los idiomas que tienen diccionario. Vive AQUÍ —el módulo ligero del
 *  entry— y no en `i18n.ts` porque el entry (`index.ts`) necesita decidir
 *  el idioma sin arrastrar los tres diccionarios (~35 KB) a `embed.js`,
 *  que tiene presupuesto. `DICTS` en i18n.ts se teclea con estos mismos y
 *  un check de tipo allí garantiza que no se separen. */
export const LOCALES = ['es', 'en', 'pt'] as const;

/** Detecta el idioma del cliente (host → `<html lang>` → navegador) y lo
 *  fija. UN SOLO detector, y por eso UN SOLO estado `localeActivo` —el que
 *  lee `t()`—: lo llaman `resolveLocale` (i18n.ts, desde el chunk
 *  perezoso) y el ENTRY (index.ts), que lo necesita PRONTO para que el
 *  botón de la ficha no se pinte en castellano antes de que corra
 *  resolveLocale. Un idioma sin diccionario cae a castellano, que es el
 *  genérico. */
export function detectarYFijarLocale(host?: { dataset?: DOMStringMap } | null): string {
  const explicit = (host?.dataset?.locale ?? '').toLowerCase();
  const docLang = (typeof document !== 'undefined' ? document.documentElement.lang : '').toLowerCase();
  const navLang = (typeof navigator !== 'undefined' ? navigator.language : '').toLowerCase();
  const candidate = explicit || docLang || navLang || 'es';
  const short = candidate.slice(0, 2);
  if ((LOCALES as readonly string[]).includes(short)) {
    fijarLocaleActivo(short, candidate);
    return short;
  }
  fijarLocaleActivo('es', 'es');
  return 'es';
}

/** El idioma en el que se está pintando. Dos letras. */
export function localeDelCliente(): string {
  return localeActivo;
}

/** Sufijo `&locale=xx` para las URLs de `by-handle`, VACÍO en castellano.
 *
 *  Es el gemelo ligero de `sufijoLocaleUrl()` de `i18n.ts`: lee el mismo
 *  estado, pero vive aquí para que el ENTRY lo pueda usar sin arrastrar
 *  los diccionarios (el presupuesto de `bundle-budget.test.ts`).
 *
 *  Sin él, `fetchTriggerContext` preguntaba por el handle SIN idioma y el
 *  `elegirPorLocale` del API caía al grupo GENÉRICO: en /pt-pt/ la sonda
 *  resolvía la plantilla CASTELLANA gemela. No se veía —la pt es un clon,
 *  así que el motor coincide, y el documento se salvaba porque
 *  `urlDeLaPlantilla` ya vuelve a preguntar por handle+locale— pero fijaba
 *  `mount.templateId` con el id ES, y un producto vinculado SÓLO en
 *  portugués sí daba 404 y `seguro: false`. Memoria
 *  [complementos-404-en-portugues]. */
export function sufijoLocaleLigero(): string {
  return localeActivo === 'es' ? '' : `&locale=${encodeURIComponent(localeActivo)}`;
}

/** La etiqueta COMPLETA del idioma activo («pt-PT», «es»). Es lo que
 *  viaja al API en `&lang=`; ver `leerIdioma` en geocode-resultados.ts. */
export function etiquetaDeIdioma(): string {
  return etiquetaActiva;
}
